import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useTier } from '@/context/TierContext';
import MobileNav from '@/components/MobileNav';
import LockedFeature from '@/components/LockedFeature';
import { Brain, Zap, Lock, Send, ChevronRight, Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_COLORS = {
  premium: 'border-orange-500/30 bg-orange-500/10 text-orange-400',
  platinum: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
};

export default function ProAnalysis() {
  const { token } = useAuth();
  const { tier, hasAccess } = useTier();
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('types'); // 'types' | 'result' | 'history'

  useEffect(() => {
    fetchTypes();
    fetchHistory();
  }, []);

  const fetchTypes = async () => {
    try {
      const res = await axios.get(`${API}/analysis/pro/types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysisTypes(res.data.analyses || []);
    } catch (err) {
      console.error('Failed to fetch analysis types:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/analysis/pro/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const handleAnalyze = async () => {
    if (!selected || !userInput.trim()) {
      toast.error('Veuillez sélectionner une analyse et remplir les informations');
      return;
    }
    if (selected.locked) {
      toast.error('Abonnement requis pour cette analyse');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/analysis/pro`,
        { analysis_type: selected.type, user_input: userInput.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      setTab('result');
      toast.success('Analyse terminée !');
      fetchHistory();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de l\'analyse';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const hasPremium = hasAccess('premium');

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="pro-analysis" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-8 animate-slide-up">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <span className="text-purple-400 font-bold text-sm uppercase tracking-widest">Analyse Pro</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-black text-white mb-2">
            Analyses <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Institutionnelles</span>
          </h1>
          <p className="text-gray-400">Les mêmes frameworks que Goldman Sachs, Morgan Stanley, BlackRock — pour vous.</p>
        </div>

        {/* Tier Info */}
        {!hasPremium && (
          <div className="glass rounded-2xl p-5 mb-6 border border-orange-500/20">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-orange-400" />
                <div>
                  <p className="font-semibold text-white text-sm">Fonctionnalité Premium</p>
                  <p className="text-gray-400 text-xs">Passez à Premium ou Pro pour accéder aux analyses institutionnelles</p>
                </div>
              </div>
              <Button
                onClick={() => window.location.href = '/pricing'}
                size="sm"
                className="bg-[#ff6b35] text-black font-bold"
              >
                Voir les plans →
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'types', label: 'Choisir une analyse' },
            { id: 'result', label: 'Résultat', disabled: !result },
            { id: 'history', label: `Historique (${history.length})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => !t.disabled && setTab(t.id)}
              disabled={t.disabled}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                tab === t.id ? 'bg-purple-500 text-white' : t.disabled ? 'text-gray-600 cursor-not-allowed' : 'glass text-gray-400 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'types' && (
          <div className="grid md:grid-cols-2 gap-4">
            {analysisTypes.map((analysis) => {
              const isSelected = selected?.type === analysis.type;
              const tierColor = TIER_COLORS[analysis.tier_required] || TIER_COLORS.premium;

              return (
                <div
                  key={analysis.type}
                  data-testid={`analysis-type-${analysis.type}`}
                  onClick={() => !analysis.locked && setSelected(analysis)}
                  className={`relative glass rounded-2xl p-5 transition-all ${
                    analysis.locked
                      ? 'opacity-60 cursor-not-allowed border border-gray-800'
                      : isSelected
                      ? 'border-purple-500/50 cursor-pointer bg-purple-500/5'
                      : 'border-transparent cursor-pointer hover:border-purple-500/30'
                  }`}
                >
                  {/* Tier badge */}
                  <div className={`absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${tierColor}`}>
                    {analysis.locked && <Lock className="w-2.5 h-2.5" />}
                    {analysis.tier_required === 'premium' ? 'Premium' : 'Pro'}
                  </div>

                  <div className="flex items-start gap-3 pr-16">
                    <span className="text-2xl flex-shrink-0">{analysis.icon}</span>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-bold text-sm mb-1 ${isSelected ? 'text-purple-300' : 'text-white'}`}>
                        {analysis.name}
                      </h3>
                      <p className="text-gray-400 text-xs">{analysis.description}</p>
                    </div>
                  </div>

                  {isSelected && !analysis.locked && (
                    <div className="mt-4 flex items-center gap-1.5 text-purple-400 text-xs font-semibold">
                      <Zap className="w-3 h-3" />
                      Sélectionnée
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Input + Run */}
        {tab === 'types' && selected && !selected.locked && (
          <div className="mt-6 glass rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">{selected.icon}</span>
              <h3 className="font-bold text-white">{selected.name}</h3>
            </div>
            <label className="block text-sm text-gray-400 mb-2">{selected.user_input_label}</label>
            <Textarea
              data-testid="pro-analysis-input"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder="Décrivez votre situation, vos objectifs, le ticker ou le secteur..."
              className="bg-[#0a0a0a] border-gray-700 text-white min-h-[120px] mb-4"
            />
            <Button
              data-testid="run-analysis-btn"
              onClick={handleAnalyze}
              disabled={loading || !userInput.trim()}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-4"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analyse en cours (30-60 secondes)...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  Lancer l'Analyse Institutionnelle
                </>
              )}
            </Button>
            <p className="text-xs text-gray-600 text-center mt-2">
              Propulsé par Claude 4 Sonnet (Anthropic) • Pour fins éducatives uniquement
            </p>
          </div>
        )}

        {/* Result Tab */}
        {tab === 'result' && result && (
          <div className="glass rounded-2xl overflow-hidden border border-purple-500/20">
            <div className="p-5 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-400" />
                <h2 className="font-bold text-white">{result.analysis_name}</h2>
              </div>
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(result.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className="p-6 prose prose-invert prose-sm max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({children}) => <h1 className="text-xl font-black text-white mb-3">{children}</h1>,
                  h2: ({children}) => <h2 className="text-lg font-bold text-purple-400 mt-5 mb-2">{children}</h2>,
                  h3: ({children}) => <h3 className="text-base font-semibold text-gray-200 mt-3 mb-1">{children}</h3>,
                  p: ({children}) => <p className="text-gray-300 leading-relaxed mb-3">{children}</p>,
                  strong: ({children}) => <strong className="text-white font-bold">{children}</strong>,
                  ul: ({children}) => <ul className="list-disc list-inside text-gray-300 space-y-1 mb-3">{children}</ul>,
                  ol: ({children}) => <ol className="list-decimal list-inside text-gray-300 space-y-1 mb-3">{children}</ol>,
                  li: ({children}) => <li className="text-gray-300">{children}</li>,
                  table: ({children}) => <div className="overflow-x-auto mb-4"><table className="w-full border-collapse text-sm">{children}</table></div>,
                  thead: ({children}) => <thead className="bg-purple-500/10">{children}</thead>,
                  th: ({children}) => <th className="text-left p-2 text-purple-400 font-semibold border-b border-purple-500/20">{children}</th>,
                  td: ({children}) => <td className="p-2 text-gray-300 border-b border-gray-800">{children}</td>,
                  blockquote: ({children}) => <blockquote className="border-l-4 border-purple-500 pl-4 text-gray-400 italic my-3">{children}</blockquote>,
                  code: ({children}) => <code className="bg-gray-900 text-purple-300 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>,
                }}
              >
                {result.result}
              </ReactMarkdown>
            </div>
            <div className="p-4 bg-purple-500/5 border-t border-gray-800">
              <p className="text-xs text-gray-500 text-center">
                Analyse générée par IA à des fins éducatives uniquement. Ne constitue pas un conseil financier.
              </p>
            </div>
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div className="space-y-3">
            {history.length === 0 ? (
              <div className="glass rounded-2xl p-12 text-center">
                <Brain className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-400">Aucune analyse dans votre historique</p>
              </div>
            ) : (
              history.map((h, i) => (
                <div
                  key={i}
                  className="glass rounded-xl p-4 cursor-pointer hover:border-purple-500/30 border border-transparent transition-all"
                  onClick={() => { setResult(h); setTab('result'); }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-white text-sm">{h.analysis_name}</div>
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{h.user_input}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {new Date(h.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
